import type { Moment } from 'moment'
import { DATE_SEARCH_OPTION_IDS, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import { boundActivityDisplayText } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityDisplayText'

export interface CarouselDateFilterAppliedParams {
  dateMode: DATE_SEARCH_OPTION_IDS
  checkIn: Moment | undefined
  checkOut: Moment | undefined
  flexibleMonths: string
  flexibleNights: FLEXIBLE_DURATION_RANGE | undefined
}

// What the filter actually means once the UI's own "is this really usable" rules are applied --
// dateMode alone isn't enough. useCarouselDateFilter's chipLabel only renders Specific text once
// both checkIn and checkOut exist, and only renders Flexible text once flexibleMonths or
// flexibleNights is set; anything else (mode switched but nothing chosen yet, or a stray
// single-date pick that never promoted the mode) reads as Anytime to the customer. Canonicalizing
// to this shape before comparing or summarizing means the two can never disagree -- there's only
// one description of "what this filter means" for either step to consume.
type CanonicalDateFilter =
  | { kind: 'anytime' }
  | { kind: 'specific'; checkIn: Moment; checkOut: Moment }
  | { kind: 'flexible'; flexibleMonths: string; flexibleNights: FLEXIBLE_DURATION_RANGE | undefined }

// flexibleNights checked with != null, not truthiness -- '' is a real member of its type
// (CRUISE_FLEXIBLE_DURATION_RANGE.ANY_DURATION, DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY), so a
// Flexible selection carrying only that value would otherwise fail this check and canonicalize to
// 'anytime', both under-reporting a real filter and (since summarizeCanonicalDateFilter below uses
// the same truthy check) risking the gate and the renderer disagreeing on what changed -- the exact
// class of bug this canonical-form approach exists to prevent.
function canonicalize(params: CarouselDateFilterAppliedParams): CanonicalDateFilter {
  if (params.dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && params.checkIn && params.checkOut) {
    return { kind: 'specific', checkIn: params.checkIn, checkOut: params.checkOut }
  }
  if (params.dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (params.flexibleMonths || params.flexibleNights != null)) {
    return { kind: 'flexible', flexibleMonths: params.flexibleMonths, flexibleNights: params.flexibleNights }
  }
  return { kind: 'anytime' }
}

// 'day' granularity, not exact-instant equality -- matches every other same-day Moment comparison
// in this codebase (e.g. chatDates.ts, DatesDropdown.tsx). A plain `===` would wrongly treat
// "touched the calendar, picked the same day back" as a real change, since that produces a new
// Moment instance with an equal value, not the same object reference.
function isSameDay(a: Moment, b: Moment): boolean {
  return a === b || a.isSame(b, 'day')
}

function isSameCanonicalDateFilter(a: CanonicalDateFilter, b: CanonicalDateFilter): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'anytime') return true
  if (a.kind === 'specific' && b.kind === 'specific') {
    return isSameDay(a.checkIn, b.checkIn) && isSameDay(a.checkOut, b.checkOut)
  }
  if (a.kind === 'flexible' && b.kind === 'flexible') {
    return a.flexibleMonths === b.flexibleMonths && a.flexibleNights === b.flexibleNights
  }
  return false
}

// Hand-built English (numeric moment tokens only, no localised month/day names, no
// react-intl) -- activity displayText is LLM-context only, never customer-facing, and
// stays English regardless of locale, same rule as every other kind in this family.
function summarizeCanonicalDateFilter(canonical: CanonicalDateFilter): string {
  if (canonical.kind === 'specific') {
    return `${canonical.checkIn.format('YYYY-MM-DD')} to ${canonical.checkOut.format('YYYY-MM-DD')}`
  }
  if (canonical.kind === 'flexible') {
    const monthsText = canonical.flexibleMonths ? canonical.flexibleMonths.split(',').join(', ') : 'unspecified months'
    const nightsText = canonical.flexibleNights != null ? `${canonical.flexibleNights} nights` : 'unspecified nights'
    return `flexible, ${nightsText}, in ${monthsText}`
  }
  return 'Anytime'
}

// Returns undefined for a no-op apply -- either the Apply button has no disabled state for
// "unchanged from current" (it's only ever disabled for an incomplete Specific range), or the
// change is invisible to the customer (e.g. a stray single-date pick while still in Anytime mode,
// or switching to Flexible without picking months/nights -- both canonicalize to the same
// "Anytime" the chip already showed before the click).
export function buildCarouselDateFilterAppliedActivityEntry(
  carouselTitle: string,
  previous: CarouselDateFilterAppliedParams,
  next: CarouselDateFilterAppliedParams,
): TripPlannerSurfaceActivityEntry | undefined {
  const previousCanonical = canonicalize(previous)
  const nextCanonical = canonicalize(next)
  if (isSameCanonicalDateFilter(previousCanonical, nextCanonical)) return undefined

  return {
    kind: 'carousel_filter_applied',
    label: 'Adjusted carousel filter',
    displayText: boundActivityDisplayText(`Adjusted date filter for "${carouselTitle}" to ${summarizeCanonicalDateFilter(nextCanonical)}`),
    sourcePath: 'trip_planner.chat.carousel_filter',
    dataClassification: 'personalised_surface',
    occurredAt: new Date().toISOString(),
  }
}
